import { currentSeasonYear } from '../utils/season';

const API_ROUTES = {
  AUTH: {
    MAIN_LOGIN: '/auth/login',
    LOGIN: '/auth/login',
    LOGIN_PLAYER: '/auth/player/login',
    REGISTER_PLAYER: '/auth/player/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    VERIFY_OTP: '/auth/verify-otp',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    PLAYER_TEAMS: (params = {}) => {
      const { page = 1, limit = 10, teamName } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (teamName) queryParams.append('teamName', teamName);
      return `/auth/player/teams?${queryParams.toString()}`;
    },
    TEAM_PLAYERS: (teamId) => `/auth/player/teams/${teamId}`,
  },
  PLAYER: {
    PROFILE: '/player/profile',
    UPDATE_PROFILE: '/player/profile/update',
    GET_ALL: '/player/all',
    GET_BY_ID: (id) => `/player/${id}`,
    DELETE: (id) => `/player/${id}`,
    // Videos
    VIDEOS: '/player/videos',
    DELETE_VIDEO: (videoId) => `/player/videos/${videoId}`,
    // Recommendation
    RECOMMENDATION: '/player/recommendation',
    DELETE_RECOMMENDATION: '/player/recommendation',
    // Strengths
    STRENGTHS: '/player/strengths',
    DELETE_STRENGTH: '/player/strengths',
    // Awards
    AWARDS: '/player/awards',
    DELETE_AWARD: '/player/awards',
    // Profile Image
    PROFILE_IMAGE: '/player/profile-image',
    DELETE_PROFILE_IMAGE: '/player/profile-image',
    // Academic Info
    ACADEMIC_INFO: '/player/academic-info',
    DELETE_ACADEMIC_INFO: '/player/academic-info',
    // Team Roster
    TEAM_ROSTER: (teamId, page = 1, limit = 20) => `/player/team-roster/${teamId}?page=${page}&limit=${limit}`,
    // Notifications
    GET_NOTIFICATIONS: '/player/notifications',
    MARK_NOTIFICATION_READ: (id) => `/player/notifications/${id}/read`,
    MARK_ALL_NOTIFICATIONS_READ: '/player/notifications/read-all',
  },
  COACH: {
    PROFILE: '/coach/profile',
    UPDATE_PROFILE: '/coach/profile/update',
    GET_ALL: '/coach/all',
    GET_BY_ID: (id) => `/coach/${id}`,
    DELETE: (id) => `/coach/${id}`,
    // Profile Image
    PROFILE_IMAGE: '/coach/profile-image',
    DELETE_PROFILE_IMAGE: '/coach/profile-image',
    // Password Reset
    FORGOT_PASSWORD: '/coach/forgot-password',
    VERIFY_OTP: '/coach/verify-otp',
    RESET_PASSWORD: '/coach/reset-password',
    // Dashboard
    DASHBOARD: '/coach/dashboard',
    SUGGESTIONS: '/coach/suggestions',
    // Staff
    STAFF: '/coach/staff',
    ADD_STAFF: '/coach/staff/add',
    // Teams
    TEAMS: (params = {}) => {
      const { page = 1, limit = 10, teamName } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (teamName) queryParams.append('teamName', teamName);
      return `/auth/player/teams?${queryParams.toString()}`;
    },
    TEAM_ROSTER: (teamId, page = 1, limit = 10, seasonYear = currentSeasonYear(), filters = null, sortBy = null, sortOrder = null, statsType = null, category = null, scope = null) => {
      const params = new URLSearchParams({ page, limit });
      if (seasonYear) params.append('seasonYear', seasonYear);
      if (sortBy) params.append('sortBy', sortBy);
      if (sortOrder) params.append('sortOrder', sortOrder);
      if (statsType) params.append('statsType', statsType);
      if (scope) params.append('scope', scope);
      if (category) params.append('category', category);
      // Add filter ranges as query params using backend field names
      if (filters && typeof filters === 'object') {
        const isPitching = statsType === 'pitching';
        const isFielding = statsType === 'fielding';
        const filterMapping = {
          // Hitting Stats
          'avg': 'batting_average',
          'obp': 'on_base_percentage',
          'slg': 'slugging_percentage',
          'ops': 'on_base_plus_slugging',
          'runs': isPitching ? 'runs_allowed' : 'runs',
          'r': isPitching ? 'runs_allowed' : 'runs',
          'hits': isPitching ? 'hits_allowed' : 'hits',
          'h': isPitching ? 'hits_allowed' : 'hits',
          '2b': isPitching ? 'doubles_allow' : 'doubles',
          '3b': isPitching ? 'triples_allowed' : 'triples',
          'hr': isPitching ? 'home_runs_allow' : 'home_runs',
          'rbi': 'rbi',
          'tb': 'total_base',
          'bb': isPitching ? 'walks_allowed' : 'walks',
          'k': isPitching ? 'strikeouts_pitched' : 'strikeouts',
          'k%': 'strikeout_percentage',
          'bb%': 'walk_percentage',
          'iso': 'iso',
          'k/bb': 'k_bb_ratio',
          'k-bb%': 'K_BB_percent',
          'ops+': 'ops_plus',
          'wrc+': 'wRC_plus',
          'woba': 'wOBA',
          'babip': 'BABIP',
          'sb': 'stolen_bases',
          'cs': 'caught_stealing',
          'ab': isPitching ? 'at_bats_against' : 'at_bats',
          'pa': 'plate_appearances',
          'hbp': isPitching ? 'hit_batters' : 'hit_by',
          'sac': 'sacrifice_hit',
          'sf': 'sacrifice_flie',
          'gp': isFielding ? 'fielding_games' : (isPitching ? 'games_pitch' : 'games_play'),
          'gs': 'games_start',
          // Pitching Stats
          'era': 'era',
          'era+': 'ERA_plus',
          'app': 'appearances',
          'w': 'wins',
          'l': 'losses',
          'so': 'strikeouts_pitched',
          'ip': 'innings_pitched',
          'whip': 'WHIP',
          'cg': 'complete_game',
          'sho': 'shutouts',
          'sv': 'saves',
          'er': 'earn_run',
          'baa': 'batting_average_against',
          'wp': 'wild_pitche',
          'bk': 'balks',
          'fip': 'FIP',
          'fip+': 'FIP_plus',
          'xfip': 'xfip',
          'lob%': 'LOB_percent',
          'babip-against': 'babip_against',
          'k/9': 'k_per_9',
          'bb/9': 'bb_per_9',
          'h/9': 'h_per_9',
          'hr/9': 'hr_per_9',
          // Fielding Stats
          'fld%': 'fielding_percentage',
          'e': 'errors',
          'po': 'putouts',
          'a': 'assists',
          'dp': 'double_plays',
          'tc': 'total_chances',
          'pb': 'passed_ball',
          'sba': 'stolen_bases_against',
          'rcs': 'runners_caught_stealing',
          'rcs%': 'runners_caught_stealing_percentage',
          'pop-time': 'pop_time',
          'time-to-1b': 'time_to_first_base',
        };
        Object.entries(filters).forEach(([key, value]) => {
          const backendKey = filterMapping[key] || key;
          if (value.min !== undefined && value.min !== '') {
            params.append(`${backendKey}_min`, value.min);
          }
          if (value.max !== undefined && value.max !== '') {
            params.append(`${backendKey}_max`, value.max);
          }
        });
      }
      return `/coach/team-roster/${teamId}?${params.toString()}`;
    },
    // Follow/Unfollow Players
    FOLLOW: (playerId) => `/coach/follow/${playerId}`,
    UNFOLLOW: (playerId) => `/coach/unfollow/${playerId}`,
    FOLLOWING: (page = 1, limit = 10) => `/coach/following?page=${page}&limit=${limit}`,
    FOLLOWERS: (page = 1, limit = 10) => `/coach/followers?page=${page}&limit=${limit}`,
    // Follow/Unfollow Teams
    FOLLOW_TEAM: (teamId) => `/coach/follow-team/${teamId}`,
    UNFOLLOW_TEAM: (teamId) => `/coach/unfollow-team/${teamId}`,
    FOLLOWED_TEAMS: (page = 1, limit = 20) => `/coach/followed-teams?page=${page}&limit=${limit}`,
    // Uncommitted Tracker
    UNCOMMITTED_PLAYERS: (params = {}) => {
      const { page = 1, limit = 10, category, metric, position, commitmentStatus, name, seasonYear, filters, sortBy, sortOrder, conference, region, division, classYear, statType, statsType, teamId, teamName, schoolName, dateFrom, dateTo, handedness, splits, state, qualifiedPlayer } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (category) queryParams.append('category', category);
      if (metric) queryParams.append('metric', metric);
      if (position && position !== 'All') queryParams.append('position', position);
      if (commitmentStatus) queryParams.append('commitmentStatus', commitmentStatus);
      if (name) queryParams.append('name', name);
      if (seasonYear) queryParams.append('seasonYear', seasonYear);
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      // Player filter modal params
      if (conference) queryParams.append('conference', conference);
      if (region) queryParams.append('region', region);
      if (division) queryParams.append('division', division);
      if (classYear) queryParams.append('class', classYear);
      if (statType) queryParams.append('statType', statType);
      if (statsType) queryParams.append('statsType', statsType);
      if (teamId) queryParams.append('teamId', teamId);
      if (teamName) queryParams.append('teamName', teamName);
      if (schoolName) queryParams.append('schoolName', schoolName);
      if (state) queryParams.append('state', state);
      if (handedness) queryParams.append('handedness', handedness);
      if (splits) queryParams.append('splits', splits);
      // Date range params
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);
      // Qualified player filter
      queryParams.append('qualifiedPlayer', qualifiedPlayer ?? false);
      // Add filter ranges as query params using backend field names
      if (filters && typeof filters === 'object') {
        const isPitching = statsType === 'pitching';
        const isFielding = statsType === 'fielding';
        // Mapping from UI filter ID to backend parameter name
        const filterMapping = {
          // Hitting Stats
          'avg': 'batting_average',
          'obp': 'on_base_percentage',
          'slg': 'slugging_percentage',
          'ops': 'on_base_plus_slugging',
          'runs': isPitching ? 'runs_allowed' : 'runs',
          'r': isPitching ? 'runs_allowed' : 'runs',
          'hits': isPitching ? 'hits_allowed' : 'hits',
          'h': isPitching ? 'hits_allowed' : 'hits',
          '2b': isPitching ? 'doubles_allow' : 'doubles',
          '3b': isPitching ? 'triples_allowed' : 'triples',
          'hr': isPitching ? 'home_runs_allow' : 'home_runs',
          'rbi': 'rbi',
          'tb': 'total_base',
          'bb': isPitching ? 'walks_allowed' : 'walks',
          'k': isPitching ? 'strikeouts_pitched' : 'strikeouts',
          'k%': 'strikeout_percentage',
          'bb%': 'walk_percentage',
          'iso': 'iso',
          'k/bb': 'k_bb_ratio',
          'ops+': 'ops_plus',
          'sb': 'stolen_bases',
          'cs': 'caught_stealing',
          'ab': isPitching ? 'at_bats_against' : 'at_bats',
          'pa': 'plate_appearances',
          'hbp': isPitching ? 'hit_batters' : 'hit_by',
          'sac': 'sacrifice_hit',
          'sf': 'sacrifice_flie',
          'gp': isFielding ? 'fielding_games' : (isPitching ? 'games_pitch' : 'games_play'),
          'gs': 'games_start',
          // Pitching Stats
          'era': 'era',
          'app': 'appearances',
          'w': 'wins',
          'l': 'losses',
          'so': 'strikeouts_pitched',
          'ip': 'innings_pitched',
          'whip': 'whip',
          'cg': 'complete_game',
          'sho': 'shutouts',
          'sv': 'saves',
          'er': 'earn_run',
          'baa': 'batting_average_against',
          'wp': 'wild_pitche',
          'bk': 'balks',
          'k/9': 'k_per_9',
          'bb/9': 'bb_per_9',
          'h/9': 'h_per_9',
          'hr/9': 'hr_per_9',
          // Fielding Stats
          'fld%': 'fielding_percentage',
          'e': 'errors',
          'po': 'putouts',
          'a': 'assists',
          'dp': 'double_plays',
          'tc': 'total_chances',
          'pb': 'passed_ball',
          'sba': 'stolen_bases_against',
          'rcs': 'runners_caught_stealing',
          'rcs%': 'runners_caught_stealing_percentage',
          'pop-time': 'pop_time',
          'time-to-1b': 'time_to_first_base',
          'wrc+': 'wRC_plus',
          // Advanced Hitting
          'woba': 'wOBA',
          'babip': 'BABIP',
          // Advanced Pitching
          'era+': 'ERA_plus',
          'fip': 'FIP',
          'fip+': 'FIP_plus',
          'xfip': 'xfip',
          'lob%': 'LOB_percent',
          'k-bb%': 'K_BB_percent',
          'babip-against': 'babip_against',
        };
        Object.entries(filters).forEach(([key, value]) => {
          const backendKey = filterMapping[key] || key;
          if (value.min !== undefined && value.min !== '') {
            queryParams.append(`${backendKey}_min`, value.min);
          }
          if (value.max !== undefined && value.max !== '') {
            queryParams.append(`${backendKey}_max`, value.max);
          }
        });
      }
      return `/coach/player-uncommitted?${queryParams.toString()}`;
    },
    // Statistics
    TOP_PLAYERS: (params = {}) => {
      const { category, metric, limit = 10, position } = params;
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit);
      if (category) queryParams.append('category', category);
      if (metric) queryParams.append('metric', metric);
      if (position && position !== 'All') queryParams.append('position', position);
      return `/coach/statistics/top-players?${queryParams.toString()}`;
    },
    // Filters
    SAVE_FILTER: '/coach/save-filter',
    GET_FILTERS: '/coach/my-filters',
    DELETE_FILTER: (filterId) => `/coach/delete-filter/${filterId}`,
    // Video Request
    VIDEO_REQUEST: '/coach/video-request',
    GET_VIDEO_REQUESTS_BY_PLAYER: (playerId) => `/coach/video-request/player/${playerId}`,
    // Video Clips
    SAVE_VIDEO_CLIP: '/coach/video-clips',
    GET_VIDEO_CLIPS: (params = {}) => {
      const { videoId, playerId, page = 1, limit = 20 } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (videoId) queryParams.append('videoId', videoId);
      if (playerId) queryParams.append('playerId', playerId);
      return `/coach/video-clips?${queryParams.toString()}`;
    },
    DELETE_VIDEO_CLIP: (clipId) => `/coach/video-clips/${clipId}`,
    // Messaging
    FOLLOWED_PLAYERS_FOR_MESSAGING: (params = {}) => {
      const { page = 1, limit = 20, search } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (search) queryParams.append('search', search);
      return `/coach/followed-players-for-messaging?${queryParams.toString()}`;
    },
  },
  SCOUT: {
    PROFILE: '/scout/profile',
    UPDATE_PROFILE: '/scout/profile/update',
    GET_ALL: '/scout/all',
    GET_BY_ID: (id) => `/scout/${id}`,
    DELETE: (id) => `/scout/${id}`,
    // Profile Image
    PROFILE_IMAGE: '/scout/profile-image',
    DELETE_PROFILE_IMAGE: '/scout/profile-image',
    // Password Reset
    FORGOT_PASSWORD: '/scout/forgot-password',
    VERIFY_OTP: '/scout/verify-otp',
    RESET_PASSWORD: '/scout/reset-password',
    // Dashboard
    DASHBOARD: '/scout/dashboard',
    SUGGESTIONS: '/scout/suggestions',
    // Teams
    TEAMS: (params = {}) => {
      const { page = 1, limit = 10, teamName } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (teamName) queryParams.append('teamName', teamName);
      return `/auth/player/teams?${queryParams.toString()}`;
    },
    TEAM_ROSTER: (teamId, page = 1, limit = 10, seasonYear = currentSeasonYear(), filters = null, sortBy = null, sortOrder = null, statsType = null, category = null, scope = null) => {
      const params = new URLSearchParams({ page, limit });
      if (seasonYear) params.append('seasonYear', seasonYear);
      if (sortBy) params.append('sortBy', sortBy);
      if (sortOrder) params.append('sortOrder', sortOrder);
      if (statsType) params.append('statsType', statsType);
      if (scope) params.append('scope', scope);
      if (category) params.append('category', category);
      // Add filter ranges as query params using backend field names
      if (filters && typeof filters === 'object') {
        const isPitching = statsType === 'pitching';
        const isFielding = statsType === 'fielding';
        const filterMapping = {
          // Hitting Stats
          'avg': 'batting_average',
          'obp': 'on_base_percentage',
          'slg': 'slugging_percentage',
          'ops': 'on_base_plus_slugging',
          'runs': isPitching ? 'runs_allowed' : 'runs',
          'r': isPitching ? 'runs_allowed' : 'runs',
          'hits': isPitching ? 'hits_allowed' : 'hits',
          'h': isPitching ? 'hits_allowed' : 'hits',
          '2b': isPitching ? 'doubles_allow' : 'doubles',
          '3b': isPitching ? 'triples_allowed' : 'triples',
          'hr': isPitching ? 'home_runs_allow' : 'home_runs',
          'rbi': 'rbi',
          'tb': 'total_base',
          'bb': isPitching ? 'walks_allowed' : 'walks',
          'k': isPitching ? 'strikeouts_pitched' : 'strikeouts',
          'k%': 'strikeout_percentage',
          'bb%': 'walk_percentage',
          'iso': 'iso',
          'k/bb': 'k_bb_ratio',
          'k-bb%': 'K_BB_percent',
          'ops+': 'ops_plus',
          'wrc+': 'wRC_plus',
          'woba': 'wOBA',
          'babip': 'BABIP',
          'sb': 'stolen_bases',
          'cs': 'caught_stealing',
          'ab': isPitching ? 'at_bats_against' : 'at_bats',
          'pa': 'plate_appearances',
          'hbp': isPitching ? 'hit_batters' : 'hit_by',
          'sac': 'sacrifice_hit',
          'sf': 'sacrifice_flie',
          'gp': isFielding ? 'fielding_games' : (isPitching ? 'games_pitch' : 'games_play'),
          'gs': 'games_start',
          // Pitching Stats
          'era': 'era',
          'era+': 'ERA_plus',
          'app': 'appearances',
          'w': 'wins',
          'l': 'losses',
          'so': 'strikeouts_pitched',
          'ip': 'innings_pitched',
          'whip': 'WHIP',
          'cg': 'complete_game',
          'sho': 'shutouts',
          'sv': 'saves',
          'er': 'earn_run',
          'baa': 'batting_average_against',
          'wp': 'wild_pitche',
          'bk': 'balks',
          'fip': 'FIP',
          'fip+': 'FIP_plus',
          'xfip': 'xfip',
          'lob%': 'LOB_percent',
          'babip-against': 'babip_against',
          'k/9': 'k_per_9',
          'bb/9': 'bb_per_9',
          'h/9': 'h_per_9',
          'hr/9': 'hr_per_9',
          // Fielding Stats
          'fld%': 'fielding_percentage',
          'e': 'errors',
          'po': 'putouts',
          'a': 'assists',
          'dp': 'double_plays',
          'tc': 'total_chances',
          'pb': 'passed_ball',
          'sba': 'stolen_bases_against',
          'rcs': 'runners_caught_stealing',
          'rcs%': 'runners_caught_stealing_percentage',
          'pop-time': 'pop_time',
          'time-to-1b': 'time_to_first_base',
        };
        Object.entries(filters).forEach(([key, value]) => {
          const backendKey = filterMapping[key] || key;
          if (value.min !== undefined && value.min !== '') {
            params.append(`${backendKey}_min`, value.min);
          }
          if (value.max !== undefined && value.max !== '') {
            params.append(`${backendKey}_max`, value.max);
          }
        });
      }
      return `/scout/team-roster/${teamId}?${params.toString()}`;
    },
    // Follow/Unfollow Players
    FOLLOW: (playerId) => `/scout/follow/${playerId}`,
    UNFOLLOW: (playerId) => `/scout/unfollow/${playerId}`,
    FOLLOWING: (page = 1, limit = 10) => `/scout/following?page=${page}&limit=${limit}`,
    FOLLOWERS: (page = 1, limit = 10) => `/scout/followers?page=${page}&limit=${limit}`,
    // Follow/Unfollow Teams
    FOLLOW_TEAM: (teamId) => `/scout/follow-team/${teamId}`,
    UNFOLLOW_TEAM: (teamId) => `/scout/unfollow-team/${teamId}`,
    FOLLOWED_TEAMS: (page = 1, limit = 20) => `/scout/followed-teams?page=${page}&limit=${limit}`,
    // Uncommitted Tracker
    UNCOMMITTED_PLAYERS: (params = {}) => {
      const { page = 1, limit = 10, category, metric, position, commitmentStatus, name, seasonYear, filters, sortBy, sortOrder, conference, region, division, classYear, statType, statsType, teamId, teamName, schoolName, dateFrom, dateTo, handedness, splits, state, qualifiedPlayer } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (category) queryParams.append('category', category);
      if (metric) queryParams.append('metric', metric);
      if (position && position !== 'All') queryParams.append('position', position);
      if (commitmentStatus) queryParams.append('commitmentStatus', commitmentStatus);
      if (name) queryParams.append('name', name);
      if (seasonYear) queryParams.append('seasonYear', seasonYear);
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (conference) queryParams.append('conference', conference);
      if (region) queryParams.append('region', region);
      if (division) queryParams.append('division', division);
      if (classYear) queryParams.append('class', classYear);
      if (statType) queryParams.append('statType', statType);
      if (statsType) queryParams.append('statsType', statsType);
      if (teamId) queryParams.append('teamId', teamId);
      if (teamName) queryParams.append('teamName', teamName);
      if (schoolName) queryParams.append('schoolName', schoolName);
      if (state) queryParams.append('state', state);
      if (handedness) queryParams.append('handedness', handedness);
      if (splits) queryParams.append('splits', splits);
      // Date range params
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);
      // Qualified player filter
      queryParams.append('qualifiedPlayer', qualifiedPlayer ?? false);
      // Add filter ranges as query params using backend field names
      if (filters && typeof filters === 'object') {
        const isPitching = statsType === 'pitching';
        const isFielding = statsType === 'fielding';
        // Mapping from UI filter ID to backend parameter name
        const filterMapping = {
          // Hitting Stats
          'avg': 'batting_average',
          'obp': 'on_base_percentage',
          'slg': 'slugging_percentage',
          'ops': 'on_base_plus_slugging',
          'runs': isPitching ? 'runs_allowed' : 'runs',
          'r': isPitching ? 'runs_allowed' : 'runs',
          'hits': isPitching ? 'hits_allowed' : 'hits',
          'h': isPitching ? 'hits_allowed' : 'hits',
          '2b': isPitching ? 'doubles_allow' : 'doubles',
          '3b': isPitching ? 'triples_allowed' : 'triples',
          'hr': isPitching ? 'home_runs_allow' : 'home_runs',
          'rbi': 'rbi',
          'tb': 'total_base',
          'bb': isPitching ? 'walks_allowed' : 'walks',
          'k': isPitching ? 'strikeouts_pitched' : 'strikeouts',
          'k%': 'strikeout_percentage',
          'bb%': 'walk_percentage',
          'iso': 'iso',
          'k/bb': 'k_bb_ratio',
          'ops+': 'ops_plus',
          'sb': 'stolen_bases',
          'cs': 'caught_stealing',
          'ab': isPitching ? 'at_bats_against' : 'at_bats',
          'pa': 'plate_appearances',
          'hbp': isPitching ? 'hit_batters' : 'hit_by',
          'sac': 'sacrifice_hit',
          'sf': 'sacrifice_flie',
          'gp': isFielding ? 'fielding_games' : (isPitching ? 'games_pitch' : 'games_play'),
          'gs': 'games_start',
          // Pitching Stats
          'era': 'era',
          'app': 'appearances',
          'w': 'wins',
          'l': 'losses',
          'so': 'strikeouts_pitched',
          'ip': 'innings_pitched',
          'whip': 'whip',
          'cg': 'complete_game',
          'sho': 'shutouts',
          'sv': 'saves',
          'er': 'earn_run',
          'baa': 'batting_average_against',
          'wp': 'wild_pitche',
          'bk': 'balks',
          'k/9': 'k_per_9',
          'bb/9': 'bb_per_9',
          'h/9': 'h_per_9',
          'hr/9': 'hr_per_9',
          // Fielding Stats
          'fld%': 'fielding_percentage',
          'e': 'errors',
          'po': 'putouts',
          'a': 'assists',
          'dp': 'double_plays',
          'tc': 'total_chances',
          'pb': 'passed_ball',
          'sba': 'stolen_bases_against',
          'rcs': 'runners_caught_stealing',
          'rcs%': 'runners_caught_stealing_percentage',
          'pop-time': 'pop_time',
          'time-to-1b': 'time_to_first_base',
          'wrc+': 'wRC_plus',
          // Advanced Hitting
          'woba': 'wOBA',
          'babip': 'BABIP',
          // Advanced Pitching
          'era+': 'ERA_plus',
          'fip': 'FIP',
          'fip+': 'FIP_plus',
          'xfip': 'xfip',
          'lob%': 'LOB_percent',
          'k-bb%': 'K_BB_percent',
          'babip-against': 'babip_against',
        };
        Object.entries(filters).forEach(([key, value]) => {
          const backendKey = filterMapping[key] || key;
          if (value.min !== undefined && value.min !== '') {
            queryParams.append(`${backendKey}_min`, value.min);
          }
          if (value.max !== undefined && value.max !== '') {
            queryParams.append(`${backendKey}_max`, value.max);
          }
        });
      }
      return `/scout/player-uncommitted?${queryParams.toString()}`;
    },
    // Statistics
    TOP_PLAYERS: (params = {}) => {
      const { category, metric, limit = 10, position } = params;
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit);
      if (category) queryParams.append('category', category);
      if (metric) queryParams.append('metric', metric);
      if (position && position !== 'All') queryParams.append('position', position);
      return `/scout/statistics/top-players?${queryParams.toString()}`;
    },
    // Filters
    SAVE_FILTER: '/scout/save-filter',
    GET_FILTERS: '/scout/my-filters',
    DELETE_FILTER: (filterId) => `/scout/delete-filter/${filterId}`,
    // Games
    GAMES: (params = {}) => {
      const { page = 1, limit = 10, startDate } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (startDate) queryParams.append('startDate', startDate);
      return `/scout/games?${queryParams.toString()}`;
    },
    // Messaging
    FOLLOWED_PLAYERS_FOR_MESSAGING: (params = {}) => {
      const { page = 1, limit = 20, search } = params;
      const queryParams = new URLSearchParams({ page, limit });
      if (search) queryParams.append('search', search);
      return `/scout/followed-players-for-messaging?${queryParams.toString()}`;
    },
    // Video Request
    VIDEO_REQUEST: '/scout/video-request',
    GET_VIDEO_REQUESTS_BY_PLAYER: (playerId) => `/scout/video-request/player/${playerId}`,
  },
  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    PENDING_APPROVALS: '/admin/approvals/pending',
    APPROVE: (id) => `/admin/approvals/${id}/approve`,
    REJECT: (id) => `/admin/approvals/${id}/reject`,
    USERS: '/admin/users',
  },
  SUPER_ADMIN: {
    DASHBOARD: '/superAdmin/dashboard',
    PENDING_APPROVALS: '/superAdmin/dashboard',
    APPROVE: (id) => `/superAdmin/approve/${id}`,
    REJECT: (id) => `/superAdmin/reject/${id}`,
    USER_DETAILS: (id) => `/superAdmin/users/${id}`,
    GET_USERS_BY_ROLES: '/superAdmin/get-users-by-roles',
    MANUAL_EDITS_USERS: '/superAdmin/manual-edits/users',
    UPDATE_USER: (id) => `/superAdmin/user/${id}/update`,
    UPLOAD_USER_VIDEO: (id) => `/superAdmin/user/${id}/upload-video`,
    DELETE_USER_VIDEO: (userId, videoId) => `/superAdmin/user/${userId}/videos/${videoId}`,
    // Games
    CREATE_GAME: '/superAdmin/game/create',
    GET_GAMES: '/superAdmin/games',
    GET_GAME_BY_ID: (id) => `/superAdmin/game/${id}`,
    UPDATE_GAME_STATUS: (id) => `/superAdmin/game/${id}/status`,
    DELETE_GAME: (id) => `/superAdmin/game/${id}`,
    // Video Requests
    GET_VIDEO_REQUESTS: '/superAdmin/video-requests',
    UPDATE_VIDEO_REQUEST: (id) => `/superAdmin/video-request/${id}`,
    // Scrape Jobs
    GET_SCRAPE_JOBS: '/superAdmin/scrape-jobs',
    GET_SCRAPE_JOB_BY_ID: (id) => `/superAdmin/scrape-jobs/${id}`,
    UPDATE_SCRAPE_JOB: (id) => `/superAdmin/scrape-jobs/${id}`,
    DELETE_SCRAPE_JOB: (id) => `/superAdmin/scrape-jobs/${id}`,
    // Notifications
    GET_NOTIFICATIONS: '/superAdmin/notifications',
    GET_NOTIFICATION_SETTINGS: '/superAdmin/notification-settings',
    UPDATE_NOTIFICATION_SETTINGS: '/superAdmin/notification-settings',
    MARK_NOTIFICATION_READ: (id) => `/superAdmin/notifications/${id}/read`,
    MARK_ALL_NOTIFICATIONS_READ: '/superAdmin/notifications/read-all',
    // Commitment Scanner
    COMMITMENT_SCANNER_STATUS: '/superAdmin/commitment-scanner/status',
    COMMITMENT_SCANS: '/superAdmin/commitment-scanner/scans',
    COMMITMENT_SCAN_UPLOAD: '/superAdmin/commitment-scanner/scans/upload',
    COMMITMENT_SCAN_DATABASE: '/superAdmin/commitment-scanner/scans/database',
    COMMITMENT_SCAN: (id) => `/superAdmin/commitment-scanner/scans/${id}`,
    COMMITMENT_SCAN_CANCEL: (id) => `/superAdmin/commitment-scanner/scans/${id}/cancel`,
    COMMITMENT_SCAN_APPLY_NEW: (id) => `/superAdmin/commitment-scanner/scans/${id}/apply-new`,
    COMMITMENT_RESULT_APPLY: (id) => `/superAdmin/commitment-scanner/results/${id}/apply`,
    COMMITMENT_RESULT_DISMISS: (id) => `/superAdmin/commitment-scanner/results/${id}/dismiss`,
    COMMITMENT_RESULT_PLAYER: (id) => `/superAdmin/commitment-scanner/results/${id}/player`,
    COMMITMENT_SCANNER_PLAYERS: '/superAdmin/commitment-scanner/players',
    // Reactivation Requests
    GET_REACTIVATION_REQUESTS: '/superAdmin/reactivation-request',
    UPDATE_REACTIVATION_REQUEST: (id) => `/superAdmin/reactivation-request/${id}`,
    // Cancellation Requests
    GET_CANCELLATION_REQUESTS: '/superAdmin/cancellation-requests',
    // Manual Edits: team profiles
    TEAMS: '/superAdmin/teams',
    UPDATE_TEAM: (id) => `/superAdmin/teams/${id}`,
    TEAM_ROSTER: (id) => `/superAdmin/teams/${id}/players`,
    // Homepage social posts
    SOCIAL_POSTS: '/superAdmin/social-posts',
    DELETE_SOCIAL_POST: (id) => `/superAdmin/social-posts/${id}`,
  },
  CONVERSATIONS: {
    START: '/conversations/start',
    MY_CONVERSATIONS: (type) => type ? `/conversations?type=${type}` : '/conversations',
    DELETE: '/conversations',
    ARCHIVE: '/conversations/archive',
    UNARCHIVE: '/conversations/unarchive',
  },
  MESSAGES: {
    GET: (conversationId, page = 1, limit = 20) => `/messages/${conversationId}?page=${page}&limit=${limit}`,
    SEND: '/messages/send',
    MARK_READ: '/messages/read',
    DELETE: (messageId) => `/messages/${messageId}`,
  },
  PAYMENT: {
    GET_PLANS: '/payment/plans',
    REGISTER_WITH_PAYMENT: '/auth/register-with-payment',
    PAYMENT_HISTORY: '/payment/payment-history',
    CANCEL_SUBSCRIPTION: '/coach/cancel-subscription',
    REACTIVATE_SUBSCRIPTION: '/payment/reactivate-subscription',
    SUBSCRIPTION_STATUS: '/payment/subscription-status',
  },
};

export default API_ROUTES;
